/**
 * Multi-format sequences (1.7.0, docs/dev/formats-1.7.0-build.md): the formats of a sequence, their default sizes and
 * ids, and documentForFormat, the one function the preview and the export both use to turn a multi-format document
 * into a plain document of one format (its size, its framing, placement and motion, without what is hidden in it).
 * Also the caption geometry shared by the preview and the burn-in host, and the platforms' approximate safe zones.
 *
 * "Same as primary": a clip with no formatFraming entry for a format (or an entry without `fit`) is framed in that
 * format exactly as in the primary (the same fit, pan, zoom and keys; the pan and keys are in the −1…1 slack space, so
 * they mean the same thing in any shape). A layer entry's `place` fields left out are the primary's scale, x and y;
 * without `motion` the layer moves as in the primary; opacity keys are always the primary's.
 *
 * Framing for a whole format (1.7.1): a format's `fit` (the primary's on the document) is the fit of every storyline clip
 * in it, over the clip's own; each clip's pan, zoom and keys for the format still apply. Left out: per clip.
 */
import { aspectLabel, sameAspect, type FitMode, type Size } from './framing.ts';
import { sequenceSizeProblem } from './sequence-formats.ts';
import { CAPTION } from './caption.ts';
import {
  MAX_FORMATS, PRIMARY_FORMAT,
  type FormatFraming, type FormatRef, type GraphicInstance, type LayerClip, type MotionKey, type SequenceDocument, type VideoClip,
} from './sequence.ts';

/* ---------------------------------------------------------------- formats */

/** A format as the editor and the export list it: the primary first, then the others in their order. */
export interface ResolvedFormat {
  /** 'primary' or the format's id. */
  id: FormatRef;
  width: number;
  height: number;
  /** The format's label, else its reduced ratio ("16:9", "9:16", "4:5", "1:1"). */
  label: string;
  /** Its reduced ratio, whatever the label ("16:9"). */
  aspect: string;
  isPrimary: boolean;
  /** The subtitle height set for it (a fraction of the height), or undefined for the default (captionLayout). */
  subtitleBottom?: number;
  /** Its framing for the whole storyline (1.7.1), or undefined for per clip. */
  fit?: FitMode;
  /** Its position in the list (0 = the primary), which also picks its outline colour. */
  index: number;
}

/** What formatsOf reads of a document. */
export type FormatsSource = Pick<SequenceDocument, 'width' | 'height' | 'formats' | 'subtitleBottom' | 'fit'>;

/** The primary and the other formats of a document. */
export function formatsOf(doc: FormatsSource): ResolvedFormat[] {
  const primary: ResolvedFormat = { id: PRIMARY_FORMAT, width: doc.width, height: doc.height, label: aspectLabel(doc), aspect: aspectLabel(doc), isPrimary: true, index: 0 };
  if (doc.subtitleBottom !== undefined) primary.subtitleBottom = doc.subtitleBottom;
  if (doc.fit) primary.fit = doc.fit;
  return [primary, ...(doc.formats ?? []).map((f, i) => {
    const r: ResolvedFormat = { id: f.id, width: f.width, height: f.height, label: f.label?.trim() || aspectLabel(f), aspect: aspectLabel(f), isPrimary: false, index: i + 1 };
    if (f.subtitleBottom !== undefined) r.subtitleBottom = f.subtitleBottom;
    if (f.fit) r.fit = f.fit;
    return r;
  })];
}

/** One format of a document by id ('primary' included), or null. */
export function sequenceFormatOf(doc: FormatsSource, id: FormatRef): ResolvedFormat | null {
  return formatsOf(doc).find((f) => f.id === id) ?? null;
}

/** The common aspects a format is added in, with their ids. */
export const COMMON_ASPECTS = [
  { id: '16x9', width: 16, height: 9, label: 'Landscape 16:9' },
  { id: '9x16', width: 9, height: 16, label: 'Vertical 9:16' },
  { id: '1x1', width: 1, height: 1, label: 'Square 1:1' },
  { id: '4x5', width: 4, height: 5, label: 'Portrait 4:5' },
] as const;

const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);

/** A format's id from its shape: 16x9, 9x16, 4x5, 1x1 (within half a percent), else the reduced WxH. */
export function formatIdLabel(s: Size): string {
  for (const a of COMMON_ASPECTS) if (sameAspect(s, a)) return a.id;
  const g = gcd(Math.round(s.width), Math.round(s.height)) || 1;
  return `${Math.round(s.width) / g}x${Math.round(s.height) / g}`;
}

/** The framing a new format of this shape starts with for its whole storyline (1.7.1): Blur for 1:1 and 4:5, else per clip. */
export function defaultFormatFit(s: Size): FitMode | undefined {
  return sameAspect(s, { width: 1, height: 1 }) || sameAspect(s, { width: 4, height: 5 }) ? 'blur' : undefined;
}

/** The framing for the whole storyline of a format of a document (1.7.1; 'primary': the document's own), or undefined for per clip. */
export function formatFitOf(doc: Pick<SequenceDocument, 'formats' | 'fit'>, formatId: FormatRef): FitMode | undefined {
  return formatId === PRIMARY_FORMAT ? doc.fit : (doc.formats ?? []).find((f) => f.id === formatId)?.fit;
}

/** An id for a new format of this shape that the document does not use yet (9x16, then 9x16-2…). */
export function newFormatId(doc: Pick<SequenceDocument, 'formats'>, s: Size, taken: string[] = []): string {
  const used = new Set([PRIMARY_FORMAT, ...(doc.formats ?? []).map((f) => f.id), ...taken]);
  const base = formatIdLabel(s);
  if (!used.has(base)) return base;
  for (let i = 2; ; i++) if (!used.has(`${base}-${i}`)) return `${base}-${i}`;
}

/**
 * The default size of a format of `aspect` beside a primary: by the primary's short side (1920×1080 → 9:16 is
 * 1080×1920, 1:1 1080×1080, 4:5 1080×1350; UHD likewise at 2160), even, and within the sequence size limits.
 */
export function defaultFormatSize(primary: Size, aspect: Size): Size {
  const short = Math.min(primary.width, primary.height);
  const even = (v: number) => Math.max(16, Math.round(v / 2) * 2);
  let w: number, h: number;
  if (aspect.width <= aspect.height) { w = short; h = (short * aspect.height) / aspect.width; } else { h = short; w = (short * aspect.width) / aspect.height; }
  // Keep it inside the model's limits, at the same shape.
  const k = Math.min(1, 7680 / w, 4320 / h);
  return { width: even(w * k), height: even(h * k) };
}

/**
 * Why a size cannot be a format of this document (or its new primary size); null when it can: the sequence size rules
 * (whole, even, within the limits), and no other format nor the primary of the same shape. `exceptId`: the format
 * being resized ('primary' for setPrimarySize).
 */
export function formatProblem(doc: Pick<SequenceDocument, 'width' | 'height' | 'formats'>, size: Size, exceptId?: FormatRef): string | null {
  const p = sequenceSizeProblem(size.width, size.height);
  if (p) return p;
  if (exceptId !== PRIMARY_FORMAT && sameAspect(size, doc)) return `The sequence is ${aspectLabel(doc)} already`;
  const clash = (doc.formats ?? []).find((f) => f.id !== exceptId && sameAspect(f, size));
  if (clash) return `There is a ${clash.label?.trim() || aspectLabel(clash)} format already`;
  if (exceptId === undefined && (doc.formats ?? []).length >= MAX_FORMATS) return `A sequence has at most ${MAX_FORMATS} other formats`;
  return null;
}

/* ---------------------------------------------------------------- resolving a format */

type FramingGroup = Pick<FormatFraming, 'fit' | 'reframeX' | 'reframeY' | 'zoom' | 'keys'>;
const FRAMING_FIELDS = ['fit', 'reframeX', 'reframeY', 'zoom', 'keys'] as const;

/** The framing of a clip in a format: the entry's own when it has `fit`, else the primary's. Fields as the clip keeps them. */
export function framingFor(clip: VideoClip | LayerClip, formatId: FormatRef): FramingGroup {
  const own = formatId !== PRIMARY_FORMAT ? clip.formatFraming?.[formatId] : undefined;
  const src: FramingGroup = own?.fit ? own : clip;
  const out: FramingGroup = {};
  if (src.fit && src.fit !== 'fit') out.fit = src.fit;
  if (src.reframeX) out.reframeX = src.reframeX;
  if (src.reframeY) out.reframeY = src.reframeY;
  if (src.zoom !== undefined && src.zoom !== 1) out.zoom = src.zoom;
  if (src.keys?.length) out.keys = src.keys;
  return out;
}

/** Whether a clip has framing of its own in a format (not the same as the primary). */
export const hasOwnFraming = (clip: VideoClip | LayerClip, formatId: FormatRef): boolean => formatId !== PRIMARY_FORMAT && Boolean(clip.formatFraming?.[formatId]?.fit);

/** A layer's static scale, x and y in a format. */
export function placeFor(clip: LayerClip, formatId: FormatRef): { scale: number; x: number; y: number } {
  const p = formatId !== PRIMARY_FORMAT ? clip.formatFraming?.[formatId]?.place : undefined;
  return { scale: p?.scale ?? clip.scale, x: p?.x ?? clip.x, y: p?.y ?? clip.y };
}

/**
 * A layer's motion keys in a format: position and size keys from the format's own `motion` when it has one (else the
 * primary's), opacity keys always from the primary's; in time order (a format's key and an opacity key may share a time).
 */
export function motionFor(clip: LayerClip, formatId: FormatRef): MotionKey[] {
  const primary = clip.motion?.keys ?? [];
  const own = formatId !== PRIMARY_FORMAT ? clip.formatFraming?.[formatId]?.motion : undefined;
  if (!own) return primary;
  const opacity: MotionKey[] = primary.filter((k) => k.opacity !== undefined).map((k) => ({ atTicks: k.atTicks, opacity: k.opacity, ease: k.ease }));
  const position = own.keys.map((k) => { const n: MotionKey = { ...k }; delete n.opacity; return n; }).filter((k) => k.x !== undefined || k.y !== undefined || k.scale !== undefined);
  const out: MotionKey[] = [];
  for (const k of [...position, ...opacity].sort((a, b) => a.atTicks - b.atTicks)) {
    // A position key and an opacity key at one time with the same ease are one key.
    const last = out.at(-1);
    const shares = last && (['x', 'y', 'scale', 'opacity'] as const).some((p) => last[p] !== undefined && k[p] !== undefined);
    if (last && last.atTicks === k.atTicks && last.ease === k.ease && !shares) out[out.length - 1] = { ...last, ...k };
    else out.push(k);
  }
  return out;
}

const hiddenIn = (item: { hideIn?: string[] }, formatId: FormatRef) => Boolean(item.hideIn?.includes(formatId));

function resolveVideo(c: VideoClip, formatId: FormatRef, wholeFit?: FitMode): VideoClip {
  const next = { ...c };
  if (formatId !== PRIMARY_FORMAT) {
    const f = framingFor(c, formatId);
    for (const k of FRAMING_FIELDS) delete next[k];
    Object.assign(next, f);
  }
  delete next.formatFraming;
  // The format's framing for its whole storyline (1.7.1) over the clip's own fit; its pan, zoom and keys stay.
  if (wholeFit === 'fit') delete next.fit; else if (wholeFit) next.fit = wholeFit;
  return next;
}

function resolveLayer(c: LayerClip, formatId: FormatRef): LayerClip {
  const next = { ...c };
  if (formatId !== PRIMARY_FORMAT) {
    const f = framingFor(c, formatId);
    for (const k of FRAMING_FIELDS) delete next[k];
    Object.assign(next, f as Partial<LayerClip>);
    Object.assign(next, placeFor(c, formatId));
    const motion = motionFor(c, formatId);
    if (motion.length) next.motion = { keys: motion }; else delete next.motion;
  }
  delete next.formatFraming;
  return next;
}

/**
 * A plain document of one format (1.7.0): the format's width and height, each clip's framing for the format in its own
 * fit, reframeX, reframeY, zoom and keys, each layer's placement and motion for it, without the layer clips and graphics
 * hidden in it, and without `formats` and `formatFraming`; the format's subtitle height as `subtitleBottom`. The preview
 * and the export both render this, so what is seen is what renders.
 *
 * For 'primary' it changes nothing else: a document without formats and hideIn comes back as it is (the same JSON).
 * Unknown format ids are resolved as the primary at the primary's size.
 *
 * 1.7.1: the format's `fit` (for the primary, the document's) is applied to every storyline clip after its own framing
 * is resolved; the result has no top-level `fit`.
 */
export function documentForFormat(doc: SequenceDocument, formatId: FormatRef): SequenceDocument {
  const format = formatId === PRIMARY_FORMAT ? null : (doc.formats ?? []).find((f) => f.id === formatId) ?? null;
  const id: FormatRef = format ? format.id : PRIMARY_FORMAT;
  const anyFraming = doc.video.some((c) => c.formatFraming) || (doc.layers ?? []).some((l) => l.clips.some((c) => c.formatFraming));
  const anyHidden = doc.graphics.some((g) => g.hideIn) || (doc.layers ?? []).some((l) => l.clips.some((c) => c.hideIn));
  if (!format && !doc.formats && !doc.fit && !anyFraming && !anyHidden) return doc;
  const wholeFit = format ? format.fit : doc.fit;
  const out: SequenceDocument = { ...doc };
  delete out.formats;
  delete out.fit;
  if (format) {
    out.width = format.width;
    out.height = format.height;
    if (format.subtitleBottom !== undefined) out.subtitleBottom = format.subtitleBottom; else delete out.subtitleBottom;
  }
  out.video = doc.video.map((c) => (c.formatFraming || format || wholeFit ? resolveVideo(c, id, wholeFit) : c));
  if (doc.layers) {
    out.layers = doc.layers.map((l) => ({
      ...l,
      clips: l.clips.filter((c) => !hiddenIn(c, id)).map((c) => (c.formatFraming || format ? resolveLayer(c, id) : c)),
    }));
  }
  out.graphics = doc.graphics.filter((g: GraphicInstance) => !hiddenIn(g, id));
  return out;
}

/* ---------------------------------------------------------------- captions */

/**
 * The subtitle line's geometry in a frame (1.7.0), shared by the preview (subtitleStyle) and the burn-in host
 * (graphics-host.ts): the font size from the short side (min(w, h) × 0.045, so landscape is as before 1.7.0 and portrait
 * no longer gets 86 px at 1080×1920), the bottom margin (7.5 % of the height for landscape and square, 20 % for
 * portrait, above the platforms' buttons; or the format's subtitle height) and the line's maximum width (80 %).
 * 1.9.0: CAPTION (with the look) is in caption.ts; cueLayout there places a cue in any of the 9 cells with its style.
 */
export function captionLayout(width: number, height: number, subtitleBottom?: number): { fontPx: number; bottomPx: number; maxWidthPx: number; bottom: number } {
  const bottom = subtitleBottom ?? (height > width ? CAPTION.bottomPortrait : CAPTION.bottomLandscape);
  return {
    fontPx: Math.round(Math.min(width, height) * CAPTION.fontOfShortSide),
    bottomPx: Math.round(height * bottom),
    maxWidthPx: Math.round(width * CAPTION.maxWidth),
    bottom,
  };
}

/* ---------------------------------------------------------------- platform safe zones */

export interface PlatformSafeZone {
  id: string;
  label: string;
  /** The aspect it applies to ("9:16", "4:5", "1:1"). */
  aspect: string;
  /** Insets as fractions of the frame: what the platform's buttons, captions and profile line usually cover. */
  insets: { top: number; right: number; bottom: number; left: number };
  /** When the values were last compared with the platforms' apps and guides (YYYY-MM). */
  checked: string;
}

/**
 * The platforms' UI-safe zones (1.7.0). APPROXIMATE: the platforms do not publish exact figures, change their layouts
 * often and differ by device; these are typical values from their creator guides and the apps as checked in
 * `checked`, to keep text and faces clear of the buttons, not a guarantee. Help says the same.
 */
export const PLATFORM_SAFE_ZONES: readonly PlatformSafeZone[] = [
  { id: 'youtube-shorts', label: 'YouTube Shorts', aspect: '9:16', insets: { top: 0.12, right: 0.16, bottom: 0.25, left: 0.06 }, checked: '2026-10' },
  { id: 'instagram-reels', label: 'Instagram Reels', aspect: '9:16', insets: { top: 0.13, right: 0.14, bottom: 0.3, left: 0.06 }, checked: '2026-10' },
  { id: 'tiktok', label: 'TikTok', aspect: '9:16', insets: { top: 0.09, right: 0.15, bottom: 0.28, left: 0.06 }, checked: '2026-10' },
  { id: 'instagram-feed-45', label: 'Instagram feed 4:5', aspect: '4:5', insets: { top: 0.05, right: 0.05, bottom: 0.08, left: 0.05 }, checked: '2026-10' },
  { id: 'instagram-feed-11', label: 'Instagram feed 1:1', aspect: '1:1', insets: { top: 0.05, right: 0.05, bottom: 0.08, left: 0.05 }, checked: '2026-10' },
];

/** The safe zones for a frame's shape. */
export const safeZonesFor = (s: Size): PlatformSafeZone[] => PLATFORM_SAFE_ZONES.filter((z) => z.aspect === aspectLabel(s));

